import axiosInstance from './axiosCreator';
import { BlockAnswers, BlockType, BlockKeyValueMap } from '../types/Study';
import { blocksLoaded, blockUpdated } from '../slices/blocks/blocksSlice';
import { store } from '../store/store';
import { todaySessionReceived } from '../slices/todaySession/todaySessionSlice';
import { setVignettesForBlock } from '../slices/vignettes/vignettesSlice';

export const getBlockById = async (blockId: string) => {
  try {
    const response = await axiosInstance.get(`/blocks/${blockId}`);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getBlockByIdForReview = async (blockId: string) => {
  try {
    const response = await axiosInstance.get(`/blocks/${blockId}/review`);
    if (
      response.data &&
      response.data.studySession &&
      response.data.block &&
      response.data.vignettes
    ) {
      const studySession = response.data.studySession;

      store.dispatch(
        todaySessionReceived({
          ...studySession,
          isReview: true,
          activeBlockId: blockId,
        })
      );

      store.dispatch(blocksLoaded([response.data.block]));

      store.dispatch(
        setVignettesForBlock({
          blockId,
          vignettes: response.data.vignettes ?? [],
        })
      );
    }
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const updateBlock = async ({
  blockId,
  name,
  type,
  answers,
  flags,
  notes,
  answerCrossouts,
  date,
  duration,
  timeLimitSeconds,
  timeRemainingSeconds,
  timeSpentSeconds,
  isStarted,
  isSubmitted,
  correctCount,
  incorrectCount,
  omittedCount,
  vignetteCount,
  timeSetting,
  ord,
  questionHighlights,
  isRecalculated,
  throwOnError,
  skipStoreUpdate,
}: {
  blockId: string;
  name?: string | null;
  type?: BlockType;
  answers?: BlockAnswers;
  flags?: BlockKeyValueMap<boolean>;
  notes?: BlockKeyValueMap<string>;
  answerCrossouts?: BlockKeyValueMap<boolean[]>;
  date?: Date;
  duration?: number;
  timeLimitSeconds?: number | null;
  timeRemainingSeconds?: number | null;
  timeSpentSeconds?: number;
  isStarted?: boolean;
  isSubmitted?: boolean;
  correctCount?: number;
  incorrectCount?: number;
  omittedCount?: number;
  vignetteCount?: number;
  timeSetting?: string;
  ord?: number;
  questionHighlights?: BlockKeyValueMap<string>;
  isRecalculated?: boolean;
  throwOnError?: boolean;
  /**
   * Skip dispatching the response into the blocks store. Background timing
   * persists (ORA-54 autosave) use this: dispatching the server's echo of a
   * seconds-old `duration` back into Redux would reset the live exam timer
   * by the request's round-trip time on every save.
   */
  skipStoreUpdate?: boolean;
}) => {
  try {
    const response = await axiosInstance.put(
      `/api/v5/qbank/blocks/${blockId}`,
      {
        ...(name !== undefined && { name }),
        ...(type !== undefined && { type }),
        ...(answers !== undefined && { answers }),
        ...(flags !== undefined && { flags }),
        ...(notes !== undefined && { notes }),
        ...(answerCrossouts !== undefined && { answerCrossouts }),
        ...(date !== undefined && { date }),
        ...(duration !== undefined && { duration }),
        ...(timeLimitSeconds !== undefined && { timeLimitSeconds }),
        ...(timeRemainingSeconds !== undefined && { timeRemainingSeconds }),
        ...(timeSpentSeconds !== undefined && { timeSpentSeconds }),
        ...(isStarted !== undefined && { isStarted }),
        ...(isSubmitted !== undefined && { isSubmitted }),
        ...(correctCount !== undefined && { correctCount }),
        ...(incorrectCount !== undefined && { incorrectCount }),
        ...(omittedCount !== undefined && { omittedCount }),
        ...(vignetteCount !== undefined && { vignetteCount }),
        ...(timeSetting !== undefined && { timeSetting }),
        ...(ord !== undefined && { ord }),
        ...(questionHighlights !== undefined && { questionHighlights }),
        ...(isRecalculated !== undefined && { isRecalculated }),
      }
    );

    const updatedBlock = response.data?.data ?? response.data;
    if (updatedBlock?.id && !skipStoreUpdate) {
      store.dispatch(blockUpdated(updatedBlock));
    }
    return updatedBlock;
  } catch (error) {
    console.error(error);
    if (throwOnError) {
      throw error;
    }
  }
};

interface SubmitBlockScoreObject {
  correctCount: number;
  incorrectCount: number;
  omittedCount: number;
}

export const submitBlock = async (
  blockId: string,
  score: SubmitBlockScoreObject,
  isTimedMode: boolean,
  isDemo: boolean
) => {
  try {
    const response = await axiosInstance.post(
      `/api/v5/qbank/blocks/${blockId}/submit`,
      {
        score,
        isTimedMode,
        isDemo,
      }
    );

    const updatedBlock = response.data?.data ?? response.data;
    if (updatedBlock?.id) {
      store.dispatch(blockUpdated(updatedBlock));
    }
    return updatedBlock;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
